import measurements from '@/constants/measurements.js';

/**
 * Helper function to generate a random value using a normal distribution.
 * @param {number} mean - The mean value (P50).
 * @param {number} stdDev - The standard deviation (approximated as (P90 - P10) / 2.56).
 * @returns {number} Randomly generated value.
 */
function randomNormal(mean, stdDev) {
  let u = 0, v = 0;
  while (u === 0) u = Math.random(); // Avoid zero
  while (v === 0) v = Math.random(); // Avoid zero
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return z * stdDev + mean;
}

/**
 * Helper function to find the nearest data point for a given age.
 * @param {Array} data - Array of [age, P10, P50, P90].
 * @param {number} age - Age in years.
 * @returns {Object} Object containing P10, P50, P90.
 */
function findNearest(data, age) {
  const clampedAge = Math.min(age, 20); // Treat ages > 20 as 20 years old
  let nearest = data[0];

  for (const entry of data) {
    if (Math.abs(entry[0] - clampedAge) < Math.abs(nearest[0] - clampedAge)) {
      nearest = entry;
    }
  }

  return {
    P10: nearest[1],
    P50: nearest[2],
    P90: nearest[3],
  };
}

/**
 * Convert height in centimeters to a string in the format "Xft, Yin".
 * @param {number} cm - Height in centimeters.
 * @returns {string} Height in imperial format.
 */
function convertHeightToImperial(cm) {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return inches === 0 ? `${feet} ft` : `${feet} ft, ${inches} in`;
}

/**
 * Convert weight in kilograms to a string in the format "Xlb".
 * @param {number} kg - Weight in kilograms.
 * @returns {string} Weight in imperial format.
 */
function convertWeightToImperial(kg) {
  const pounds = kg * 2.20462;
  return pounds < 20 ? `${pounds.toFixed(1)} lb` : `${Math.round(pounds)} lb`;
}

/**
 * Generate random measurements for a given age and sex.
 * @param {number} age - Age in years.
 * @param {string} sex - Sex ("male" or "female").
 * @returns {Object} Randomly generated height, weight, and head circumference.
 */
function generateMeasurements({ age, sex }) {
  const sexKey = sex.toLowerCase();

  // Find nearest height, weight, and head circumference data
  const heightData = findNearest(measurements.height[sexKey], age);
  const weightData = findNearest(measurements.weight[sexKey], age);
  const headCircData = age < 3 ? findNearest(measurements.head_circumference[sexKey], age) : null;

  // Calculate standard deviations
  const heightStdDev = (heightData.P90 - heightData.P10) / 2.56;
  const weightStdDev = (weightData.P90 - weightData.P10) / 2.56;
  const headCircStdDev = headCircData ? (headCircData.P90 - headCircData.P10) / 2.56 : null;

  // Generate random values using a normal distribution
  const height = randomNormal(heightData.P50, heightStdDev);
  const weight = randomNormal(weightData.P50, weightStdDev);
  const headCircumference = headCircData ? randomNormal(headCircData.P50, headCircStdDev) : null;

  // Convert to desired formats
  return {
    height: convertHeightToImperial(height),
    weight: convertWeightToImperial(weight),
    headCircumference: headCircumference !== null ? `${headCircumference.toFixed(2)} cm` : null,
  };
}

export { generateMeasurements };
